import { useTranslation } from 'react-i18next';
import TextField from '@sdb/ui/atoms/TextField';

import {
  AbsenceRolloutDutyLine,
  RegisterAbsenceWorkAgreement
} from 'RestAPIs/roster/register-absence';

import {
  ContentSection,
  CountedHeader,
  CounterBadge,
  ProposalCode,
  ProposalColumn,
  ProposalColumnHeader,
  ProposalContent,
  ProposalDate,
  ProposalEmptyState,
  ProposalHours,
  ProposalPeriod,
  ProposalRow,
  ProposalScrollArea,
  SectionPanel,
  SectionTitle,
  SplitColumn,
  SplitLayout,
  WeekdayButton,
  WeekdaySelector,
  WorkAgreementCard,
  WorkAgreementHours,
  WorkAgreementList,
  WorkAgreementPrimary,
  WorkAgreementSecondary,
  WorkAgreementSettings,
  WorkAgreementSettingsLabel,
  WorkAgreementSettingsRow
} from './LeaveRequestProposalDialog.styles';
import {
  formatDate,
  formatRolloutHours,
  formatTime,
  getRolloutCostCenter,
  getRolloutDutyCode,
  getRolloutWorkAgreementNumber,
  WorkAgreementSetting,
  WEEKDAY_OPTIONS,
  createInitialWorkAgreementSetting,
  workAgreementLabel
} from './LeaveRequestProposalDialog.utils';

interface RolloutViewProps {
  workAgreement: RegisterAbsenceWorkAgreement | undefined;
  workAgreementSettings: Record<number, WorkAgreementSetting>;
  existingRolloutDuties: AbsenceRolloutDutyLine[];
  newRolloutDuties: AbsenceRolloutDutyLine[];
  onUpdateWorkAgreementSetting: (
    waId: number,
    partial: Partial<WorkAgreementSetting>
  ) => void;
}

const RolloutView = ({
  workAgreement,
  workAgreementSettings,
  existingRolloutDuties,
  newRolloutDuties,
  onUpdateWorkAgreementSetting
}: RolloutViewProps) => {
  const { t } = useTranslation('common');

  const getRolloutActionLabel = (entry: AbsenceRolloutDutyLine) =>
    t(
      `plannerMessages.leaveRequestDialog.rolloutActions.${entry.action ?? ''}`,
      {
        defaultValue: entry.action ?? '-'
      }
    );

  return (
    <SplitLayout>
      <SplitColumn>
        <ContentSection>
          <SectionTitle>
            {t('plannerMessages.leaveRequestDialog.workAgreementsTitle')}
          </SectionTitle>
          <SectionPanel>
            {!workAgreement ? (
              <ProposalEmptyState>
                {t('plannerMessages.leaveRequestDialog.noWorkAgreements')}
              </ProposalEmptyState>
            ) : (
              <WorkAgreementList>
                {(() => {
                  const settings =
                    workAgreementSettings[workAgreement.waId] ??
                    createInitialWorkAgreementSetting(workAgreement);

                  return (
                    <WorkAgreementCard key={workAgreement.waId} selected>
                      <div>
                        <WorkAgreementPrimary>
                          {workAgreementLabel(workAgreement)}
                        </WorkAgreementPrimary>
                        <WorkAgreementSecondary>
                          {t(
                            'plannerMessages.leaveRequestDialog.workAgreement'
                          )}
                        </WorkAgreementSecondary>
                      </div>
                      <WorkAgreementHours>
                        {formatRolloutHours(
                          workAgreement.hoursPerWeek ??
                            workAgreement.weekHoursMin
                        )}
                      </WorkAgreementHours>

                      <WorkAgreementSettings>
                        <WorkAgreementSettingsRow>
                          <WorkAgreementSettingsLabel>
                            {t(
                              'plannerMessages.leaveRequestDialog.rolloutStartDate'
                            )}
                          </WorkAgreementSettingsLabel>
                          <TextField
                            type="date"
                            size="small"
                            value={settings.rolloutStartDate ?? ''}
                            onChange={(event) =>
                              onUpdateWorkAgreementSetting(workAgreement.waId, {
                                rolloutStartDate: event.target.value || null
                              })
                            }
                          />
                        </WorkAgreementSettingsRow>

                        <WorkAgreementSettingsRow>
                          <WorkAgreementSettingsLabel>
                            {t(
                              'plannerMessages.leaveRequestDialog.rolloutDays'
                            )}
                          </WorkAgreementSettingsLabel>
                          <WeekdaySelector>
                            {WEEKDAY_OPTIONS.map((weekday) => {
                              const daySelected =
                                settings.inputDays.includes(weekday);

                              const isLastSelected =
                                daySelected && settings.inputDays.length === 1;

                              return (
                                <WeekdayButton
                                  key={`${workAgreement.waId}-${weekday}`}
                                  type="button"
                                  selected={daySelected}
                                  disabled={isLastSelected}
                                  onClick={() =>
                                    onUpdateWorkAgreementSetting(
                                      workAgreement.waId,
                                      {
                                        inputDays: daySelected
                                          ? settings.inputDays.filter(
                                              (value) => value !== weekday
                                            )
                                          : [...settings.inputDays, weekday]
                                      }
                                    )
                                  }
                                >
                                  {t(
                                    `plannerMessages.leaveRequestDialog.weekdays.${weekday}`
                                  )}
                                </WeekdayButton>
                              );
                            })}
                          </WeekdaySelector>
                        </WorkAgreementSettingsRow>
                      </WorkAgreementSettings>
                    </WorkAgreementCard>
                  );
                })()}
              </WorkAgreementList>
            )}
          </SectionPanel>
        </ContentSection>
      </SplitColumn>

      <SplitColumn>
        <ProposalColumn>
          <CountedHeader>
            <ProposalColumnHeader>
              {t('plannerMessages.leaveRequestDialog.rolloutExistingDuties')}
            </ProposalColumnHeader>
            <CounterBadge>{existingRolloutDuties.length}</CounterBadge>
          </CountedHeader>
          {existingRolloutDuties.length === 0 ? (
            <ProposalEmptyState>
              {t('plannerMessages.leaveRequestDialog.noExistingDuties')}
            </ProposalEmptyState>
          ) : (
            <ProposalScrollArea>
              {existingRolloutDuties.map((entry, index) => (
                <ProposalRow key={`${entry.startTime}-${index}-existing`}>
                  <ProposalDate>{formatDate(entry.startTime)}</ProposalDate>
                  <ProposalCode>{getRolloutDutyCode(entry)}</ProposalCode>
                  <ProposalHours>
                    {formatRolloutHours(entry.totalHours)}
                  </ProposalHours>
                  <ProposalContent>
                    {getRolloutWorkAgreementNumber(entry) ? (
                      <ProposalPeriod>
                        {t('plannerMessages.leaveRequestDialog.workAgreement')}:{' '}
                        {getRolloutWorkAgreementNumber(entry)}
                      </ProposalPeriod>
                    ) : null}
                    {getRolloutCostCenter(entry) ? (
                      <ProposalPeriod>
                        {getRolloutCostCenter(entry)}
                      </ProposalPeriod>
                    ) : null}
                    <ProposalPeriod>
                      {formatTime(entry.startTime)} -{' '}
                      {formatTime(entry.endTime)}
                    </ProposalPeriod>
                    <ProposalPeriod>
                      {getRolloutActionLabel(entry)}
                    </ProposalPeriod>
                  </ProposalContent>
                </ProposalRow>
              ))}
            </ProposalScrollArea>
          )}
        </ProposalColumn>

        <ProposalColumn>
          <CountedHeader>
            <ProposalColumnHeader>
              {t('plannerMessages.leaveRequestDialog.rolloutNewDuties')}
            </ProposalColumnHeader>
            <CounterBadge>{newRolloutDuties.length}</CounterBadge>
          </CountedHeader>
          {newRolloutDuties.length === 0 ? (
            <ProposalEmptyState>
              {t('plannerMessages.leaveRequestDialog.noRolloutDuties')}
            </ProposalEmptyState>
          ) : (
            <ProposalScrollArea>
              {newRolloutDuties.map((entry, index) => (
                <ProposalRow key={`${entry.startTime}-${index}-new`}>
                  <ProposalDate>{formatDate(entry.startTime)}</ProposalDate>
                  <ProposalCode>{getRolloutDutyCode(entry)}</ProposalCode>
                  <ProposalHours>
                    {formatRolloutHours(entry.totalHours)}
                  </ProposalHours>
                  <ProposalContent>
                    {getRolloutWorkAgreementNumber(entry) ? (
                      <ProposalPeriod>
                        {t('plannerMessages.leaveRequestDialog.workAgreement')}:{' '}
                        {getRolloutWorkAgreementNumber(entry)}
                      </ProposalPeriod>
                    ) : null}
                    {getRolloutCostCenter(entry) ? (
                      <ProposalPeriod>
                        {getRolloutCostCenter(entry)}
                      </ProposalPeriod>
                    ) : null}
                    <ProposalPeriod>
                      {formatTime(entry.startTime)} -{' '}
                      {formatTime(entry.endTime)}
                    </ProposalPeriod>
                    <ProposalPeriod>
                      {getRolloutActionLabel(entry)}
                    </ProposalPeriod>
                  </ProposalContent>
                </ProposalRow>
              ))}
            </ProposalScrollArea>
          )}
        </ProposalColumn>
      </SplitColumn>
    </SplitLayout>
  );
};

export default RolloutView;
